import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, endWith, map, of, switchMap, takeUntil, withLatestFrom } from 'rxjs'

import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'
import { selectAllDataModelEntities } from '@flowx/designer/shared/store/selectors/data-model.selectors'

import { AiInspectorActions } from './inspector.actions'

// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../../store/ai.actions'
import { AiSelectors } from '../../../../store/ai.selectors'
import { AiContextService } from '../../../../store/context.service'
import { AiInspectorApi, InspectProcessPayload } from '../inspector.api'

@Injectable()
export class AiInspectorEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #inspectorApi = inject(AiInspectorApi)
  #aiContextService = inject(AiContextService)
  #urlParametersStore = inject(UrlParametersStoreService)
  #toastMessageService = inject(ToastMessageService)

  inspectProcessStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.inspectProcess),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(selectAllDataModelEntities)
      ),
      map(([{ processContext }, sessionId, dataModelEntities]) => {
        const params = this.#urlParametersStore.urlParameters
        const payload: InspectProcessPayload = {
          application_id: params.appId || '',
          application_version: params.appVersionId || '',
          process_id: params.selectedProcessResourceId || '',
          process: processContext || {},
          datamodel: dataModelEntities || [],
          session_id: sessionId,
        }
        return payload
      }),
      switchMap((payload) => {
        this.#toastMessageService.showSuccessMessage('Running process inspection...')
        return this.#inspectorApi.inspectProcess(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiInspectorActions.inspectProcessStream({ response })),
          catchError(({ status }) =>
            of(AiInspectorActions.inspectProcessFailure({ error: status }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  // After inspection completes, refresh the warnings list
  onInspectComplete = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.inspectProcessComplete),
      map(({ summary }) => {
        const total = summary?.total || 0
        this.#toastMessageService.showSuccessMessage(
          `Inspection complete: ${total} misconfigurations found`
        )
        const params = this.#urlParametersStore.urlParameters
        return AiInspectorActions.getWarningsMessages({
          application_id: params.appId || '',
          application_version: params.appVersionId || '',
          process_id: params.selectedProcessResourceId || '',
          category: '',
        })
      })
    )
  )
}
